/**
 * Area `party-access` of the console gateway — over `apps/api/src/routes/party-access/` (design §40.7, §40.19.3,
 * §40.25.2). Owned by plan-029 unit **WAPP-ACCESS**: "Who may call this agent" — the VoiceAgent's access setting and the
 * people on it (ADR 0081, proposed; D-12 as changed at AP-0, DD29-21, DD29-22); WAPP-CONF (wave 7) added the assurance
 * levels "number + text message code" and "number + passcode" and the passcodes:
 *
 * ```
 *   setVoiceAgentAccessEntryPasscode     `setVoiceAgentAccessEntryPasscode`     PUT    …/access/entries/{entry_id}/passcode
 *   removeVoiceAgentAccessEntryPasscode  `removeVoiceAgentAccessEntryPasscode`  DELETE …/access/entries/{entry_id}/passcode
 * ```
 *
 * A PASSCODE GOES ONE WAY: it is sent once in the body and never read back — the entry says only `passcodeSet` and its
 * digit count.
 *
 * ```
 *   readVoiceAgentAccess          `getVoiceAgentAccess`           GET    /v1/agents/{id}/access            ETag = revision
 *   changeVoiceAgentAccess        `setVoiceAgentAccess`           PUT    /v1/agents/{id}/access            If-Match required
 *   listVoiceAgentAccessEntries   `listVoiceAgentAccessEntries`   GET    /v1/agents/{id}/access/entries    (≤ 500, whole)
 *   addVoiceAgentAccessEntry      `addVoiceAgentAccessEntry`      POST   /v1/agents/{id}/access/entries
 *   renameVoiceAgentAccessEntry   `renameVoiceAgentAccessEntry`   PATCH  /v1/agents/{id}/access/entries/{entry_id}
 *   removeVoiceAgentAccessEntry   `removeVoiceAgentAccessEntry`   DELETE /v1/agents/{id}/access/entries/{entry_id}
 * ```
 *
 * THE LOCK STATE COMES FROM THE ACCESS READ (§40.25.2; CHANGED 2026-10-28 by plan-029 ACCESS-SPLIT). The access read
 * carries `lock_state` — `pending` ("will soon answer only listed callers"), `locked` ("refuses every caller") or `none`
 * — computed by the api from its twin of the internal access lock, so every reader of the VoiceAgent sees it. Until
 * then the portal read the notice feed, which addresses only the people who may add the people.
 *
 * NUMBERS IN CLEAR ONLY TO `voice_agent.write`: an entry's `phoneE164` is `null` for everyone else, who see its last
 * three digits. Refusals raise `PartyRefusedError` (from `./caller-protection.ts`) with the rule a `400` broke.
 *
 * The register is EMPTY: every operation is in the assembled spec. The sandbox half is `../sandbox/party-access.ts`.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import { useGateway } from "../provider.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";
import { partyBodyOf } from "./caller-protection.js";

export const PARTY_ACCESS_ASSURANCE_LEVEL = ["number_only", "number_sms_code", "number_passcode", "signed_visitor"] as const;
export type PartyAccessAssuranceLevel = (typeof PARTY_ACCESS_ASSURANCE_LEVEL)[number];

/**
 * The assurance levels the portal offers (design §40.7.1). Release 1 offered `number_only` alone; Release 2 (plan-029
 * WAPP-CONF) adds `number_sms_code` and `number_passcode`, each only while "Confirm who's calling" says its method is
 * available ({@link partyAccessLevelsOffered}); `signed_visitor` waits for Release 3.
 */
export const PARTY_ACCESS_ASSURANCE_LEVEL_OFFERED: readonly PartyAccessAssuranceLevel[] = ["number_only", "number_sms_code", "number_passcode"];

/** The passcode a person may be given: 4 to 8 digits (`PUT …/passcode`). */
export const PARTY_ACCESS_PASSCODE_PATTERN = /^[0-9]{4,8}$/;

/**
 * The levels offered for this VoiceAgent now: `number_only` always; `number_sms_code` while text message codes are
 * available (switched on and priced); `number_passcode` while passcodes are. A level already stored is offered too, so
 * the choice shows what is saved even when its method is no longer available.
 */
export function partyAccessLevelsOffered(
  availability: { readonly smsCode: boolean; readonly passcode: boolean } | null,
  stored: PartyAccessAssuranceLevel | null,
): readonly PartyAccessAssuranceLevel[] {
  return PARTY_ACCESS_ASSURANCE_LEVEL_OFFERED.filter(
    (level) =>
      level === "number_only" ||
      level === stored ||
      (level === "number_sms_code" && availability?.smsCode === true) ||
      (level === "number_passcode" && availability?.passcode === true),
  );
}

export const PARTY_ACCESS_IDENTITY_KIND = ["phone", "trunk_local", "signed_visitor"] as const;
export type PartyAccessIdentityKind = (typeof PARTY_ACCESS_IDENTITY_KIND)[number];

/** At most this many people per VoiceAgent (`422 access_entry_limit_reached`). */
export const PARTY_ACCESS_ENTRY_MOST = 500;

/** "Who may call this agent" (`PartyAccess`). */
export interface PartyAccessView {
  /** `false` while choosing who may call is not available yet in this deploy: the values show, nothing can change. */
  readonly available: boolean;
  readonly revision: number;
  /** Whether the VoiceAgent answers only the people on its list. */
  readonly enabled: boolean;
  /** `null` while nothing is saved. */
  readonly assuranceLevel: PartyAccessAssuranceLevel | null;
  readonly anySignedVisitor: boolean;
  readonly entryCount: number;
  /** An internal-audience VoiceAgent: it should answer only the people on its list. */
  readonly required: boolean;
  /** Internal, and no list in force (enabled with a person, or any signed visitor): it will refuse, or refuses, everyone. */
  readonly openWarning: boolean;
  /** The internal access lock (`lock_state`): `pending` — it will soon refuse every caller; `locked` — it does. */
  readonly lockState: PartyAccessLockState;
  readonly updatedAt: string | null;
}

/** `PartyAccess.lock_state` (§40.25.2, 2026-10-28). */
export const PARTY_ACCESS_LOCK_STATE = ["none", "pending", "locked"] as const;
export type PartyAccessLockState = (typeof PARTY_ACCESS_LOCK_STATE)[number];

/** A change to the setting: every field stated (`PartyAccessChange`). */
export interface PartyAccessChange {
  readonly enabled: boolean;
  readonly assuranceLevel: PartyAccessAssuranceLevel;
  readonly anySignedVisitor: boolean;
}

/** One person on the list (`PartyAccessEntry`). */
export interface PartyAccessEntryView {
  readonly partyAccessEntryId: string;
  readonly label: string;
  readonly identityKind: PartyAccessIdentityKind;
  /** The phone number, only to people who may change the agent; otherwise `null`. */
  readonly phoneE164: string | null;
  readonly lastThreeDigit: string | null;
  /** The number as the customer's phone system sends it, only to people who may change the agent. */
  readonly trunkLocalNumber: string | null;
  readonly sipTrunkId: string | null;
  readonly signedVisitorSubject: string | null;
  readonly passcodeSet: boolean;
  readonly passcodeDigitCount: number | null;
  readonly createdAt: string;
}

/** The people on the list, oldest first, and whether the list is available in this deploy. */
export interface PartyAccessEntryPageView {
  readonly entry: readonly PartyAccessEntryView[];
  readonly available: boolean;
}

/** Who a new person is: a phone number, a number on one of the customer's SIP trunks, or a signed visitor. */
export type PartyAccessIdentityInput =
  | { readonly kind: "phone"; readonly number: string; readonly region: string | null }
  | { readonly kind: "trunk_local"; readonly sipTrunkId: string; readonly trunkLocalNumber: string }
  | { readonly kind: "signed_visitor"; readonly signedVisitorSubject: string };

/** A person to add (`PartyAccessEntryAddition`). */
export interface PartyAccessEntryAddition {
  readonly label: string;
  readonly identity: PartyAccessIdentityInput;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-029 API-PROT: "Who may call this agent", with its revision and the internal warnings. */
    readVoiceAgentAccess(voiceAgentId: string): Promise<PartyAccessView>;
    /**
     * plan-029 API-PROT: switch the list on or off, naming the revision read. Raises `PartyRefusedError`:
     * `access_list_unavailable`, `assurance_level_unavailable`, `409 revision_stale`.
     */
    changeVoiceAgentAccess(voiceAgentId: string, change: PartyAccessChange, revision: number): Promise<PartyAccessView>;
    /** plan-029 API-PROT: the people on the list, oldest first (at most 500, answered whole). */
    listVoiceAgentAccessEntries(voiceAgentId: string): Promise<PartyAccessEntryPageView>;
    /**
     * plan-029 API-PROT: add a person. Raises `PartyRefusedError` (`number_invalid`, `label_too_long`, `409
     * access_entry_exists`, `access_entry_limit_reached`, `access_list_unavailable`, `assurance_level_unavailable`).
     */
    addVoiceAgentAccessEntry(voiceAgentId: string, addition: PartyAccessEntryAddition): Promise<PartyAccessEntryView>;
    /** plan-029 API-PROT: rename a person on the list. */
    renameVoiceAgentAccessEntry(voiceAgentId: string, partyAccessEntryId: string, label: string): Promise<PartyAccessEntryView>;
    /** plan-029 API-PROT: take a person off the list. */
    removeVoiceAgentAccessEntry(voiceAgentId: string, partyAccessEntryId: string): Promise<void>;
    /**
     * plan-029 API-CONF (WAPP-CONF): give a person a passcode of 4 to 8 digits, replacing any earlier one. The api keeps
     * only a keyed digest and never answers it; nor does this method. Raises `PartyRefusedError`
     * (`validation_failed`, `passcode_unavailable`).
     */
    setVoiceAgentAccessEntryPasscode(voiceAgentId: string, partyAccessEntryId: string, passcode: string): Promise<void>;
    /** plan-029 API-CONF (WAPP-CONF): remove a person's passcode. Idempotent. */
    removeVoiceAgentAccessEntryPasscode(voiceAgentId: string, partyAccessEntryId: string): Promise<void>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type PartyAccessGatewaySlice = Pick<
  ConsoleGateway,
  | "readVoiceAgentAccess"
  | "changeVoiceAgentAccess"
  | "listVoiceAgentAccessEntries"
  | "addVoiceAgentAccessEntry"
  | "renameVoiceAgentAccessEntry"
  | "removeVoiceAgentAccessEntry"
  | "setVoiceAgentAccessEntryPasscode"
  | "removeVoiceAgentAccessEntryPasscode"
>;

/** This area's rows of `GATEWAY_GAP`: none — every operation is served. */
export const partyAccessGap: readonly GatewayGap[] = [];

// ── the wire ─────────────────────────────────────────────────────────────────────────────────────────────

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, allowed.join(" | "), raw);
  return found;
}

function nullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

/** One `PartyAccess`. */
export function partyAccessOf(wire: unknown, path: string): PartyAccessView {
  const data = readRecord(wire, path);
  return {
    available: readBoolean(data["available"], `${path}.available`),
    revision: readWholeNumber(data["revision"], `${path}.revision`),
    enabled: readBoolean(data["enabled"], `${path}.enabled`),
    assuranceLevel:
      data["assurance_level"] === null ? null : oneOf(PARTY_ACCESS_ASSURANCE_LEVEL, data["assurance_level"], `${path}.assurance_level`),
    anySignedVisitor: readBoolean(data["any_signed_visitor"], `${path}.any_signed_visitor`),
    entryCount: readWholeNumber(data["entry_count"], `${path}.entry_count`),
    required: readBoolean(data["required"], `${path}.required`),
    openWarning: readBoolean(data["open_warning"], `${path}.open_warning`),
    lockState: oneOf(PARTY_ACCESS_LOCK_STATE, data["lock_state"], `${path}.lock_state`),
    updatedAt: nullableText(data["updated_at"], `${path}.updated_at`),
  };
}

/** One `PartyAccessEntry`. */
export function partyAccessEntryOf(wire: unknown, path: string): PartyAccessEntryView {
  const row = readRecord(wire, path);
  return {
    partyAccessEntryId: readText(row["party_access_entry_id"], `${path}.party_access_entry_id`),
    label: readText(row["label"], `${path}.label`),
    identityKind: oneOf(PARTY_ACCESS_IDENTITY_KIND, row["identity_kind"], `${path}.identity_kind`),
    phoneE164: nullableText(row["phone_e164"], `${path}.phone_e164`),
    lastThreeDigit: nullableText(row["last_three_digit"], `${path}.last_three_digit`),
    trunkLocalNumber: nullableText(row["trunk_local_number"], `${path}.trunk_local_number`),
    sipTrunkId: nullableText(row["sip_trunk_id"], `${path}.sip_trunk_id`),
    signedVisitorSubject: nullableText(row["signed_visitor_subject"], `${path}.signed_visitor_subject`),
    passcodeSet: readBoolean(row["passcode_set"], `${path}.passcode_set`),
    passcodeDigitCount: row["passcode_digit_count"] === null ? null : readWholeNumber(row["passcode_digit_count"], `${path}.passcode_digit_count`),
    createdAt: readText(row["created_at"], `${path}.created_at`),
  };
}

function dataOf(body: unknown, operationId: string): unknown {
  return readRecord(body, operationId)["data"];
}

function identityBodyOf(identity: PartyAccessIdentityInput): Readonly<Record<string, unknown>> {
  switch (identity.kind) {
    case "phone":
      return { kind: "phone", number: identity.number, region: identity.region };
    case "trunk_local":
      return { kind: "trunk_local", sip_trunk_id: identity.sipTrunkId, trunk_local_number: identity.trunkLocalNumber };
    case "signed_visitor":
      return { kind: "signed_visitor", signed_visitor_subject: identity.signedVisitorSubject };
  }
}

export function partyAccessApiSlice(client: WeTalkApiClient, _context: SliceContext): PartyAccessGatewaySlice {
  return {
    async readVoiceAgentAccess(voiceAgentId) {
      const answer = await client.getVoiceAgentAccess({ path: { voice_agent_id: voiceAgentId } });
      return partyAccessOf(dataOf(partyBodyOf(answer), "getVoiceAgentAccess"), "getVoiceAgentAccess.data");
    },
    async changeVoiceAgentAccess(voiceAgentId, change, revision) {
      const answer = await client.setVoiceAgentAccess({
        path: { voice_agent_id: voiceAgentId },
        body: { enabled: change.enabled, assurance_level: change.assuranceLevel, any_signed_visitor: change.anySignedVisitor },
        header: { "if-match": `"${String(revision)}"` },
      });
      return partyAccessOf(dataOf(partyBodyOf(answer), "setVoiceAgentAccess"), "setVoiceAgentAccess.data");
    },
    async listVoiceAgentAccessEntries(voiceAgentId) {
      const body = readRecord(
        partyBodyOf(await client.listVoiceAgentAccessEntries({ path: { voice_agent_id: voiceAgentId } })),
        "listVoiceAgentAccessEntries",
      );
      return {
        entry: readArray(body["data"], "listVoiceAgentAccessEntries.data").map((one, index) =>
          partyAccessEntryOf(one, `listVoiceAgentAccessEntries.data[${String(index)}]`),
        ),
        available: readBoolean(body["available"], "listVoiceAgentAccessEntries.available"),
      };
    },
    async addVoiceAgentAccessEntry(voiceAgentId, addition) {
      const answer = await client.addVoiceAgentAccessEntry({
        path: { voice_agent_id: voiceAgentId },
        body: { label: addition.label, identity: identityBodyOf(addition.identity) },
      });
      return partyAccessEntryOf(dataOf(partyBodyOf(answer), "addVoiceAgentAccessEntry"), "addVoiceAgentAccessEntry.data");
    },
    async renameVoiceAgentAccessEntry(voiceAgentId, partyAccessEntryId, label) {
      const answer = await client.renameVoiceAgentAccessEntry({
        path: { voice_agent_id: voiceAgentId, party_access_entry_id: partyAccessEntryId },
        body: { label },
      });
      return partyAccessEntryOf(dataOf(partyBodyOf(answer), "renameVoiceAgentAccessEntry"), "renameVoiceAgentAccessEntry.data");
    },
    async removeVoiceAgentAccessEntry(voiceAgentId, partyAccessEntryId) {
      partyBodyOf(
        await client.removeVoiceAgentAccessEntry({
          path: { voice_agent_id: voiceAgentId, party_access_entry_id: partyAccessEntryId },
        }),
      );
    },
    async setVoiceAgentAccessEntryPasscode(voiceAgentId, partyAccessEntryId, passcode) {
      partyBodyOf(
        await client.setVoiceAgentAccessEntryPasscode({
          path: { voice_agent_id: voiceAgentId, party_access_entry_id: partyAccessEntryId },
          body: { passcode },
        }),
      );
    },
    async removeVoiceAgentAccessEntryPasscode(voiceAgentId, partyAccessEntryId) {
      partyBodyOf(
        await client.removeVoiceAgentAccessEntryPasscode({
          path: { voice_agent_id: voiceAgentId, party_access_entry_id: partyAccessEntryId },
        }),
      );
    },
  };
}

// ── the reads the screens use ────────────────────────────────────────────────────────────────────────────

/** Query keys of this area. */
export const PARTY_ACCESS_QUERY_KEY = {
  access: (voiceAgentId: string) => ["party-access", voiceAgentId] as const,
  entry: (voiceAgentId: string) => ["party-access", voiceAgentId, "entry"] as const,
};

/** "Who may call this agent". A refused or failed read leaves `data` undefined — never a remembered value. */
export function useVoiceAgentAccess(voiceAgentId: string): UseQueryResult<PartyAccessView> {
  const gateway = useGateway();
  return useQuery({
    queryKey: PARTY_ACCESS_QUERY_KEY.access(voiceAgentId),
    queryFn: () => gateway.readVoiceAgentAccess(voiceAgentId),
    retry: false,
  });
}

/** The people on the list. */
export function useVoiceAgentAccessEntries(voiceAgentId: string): UseQueryResult<PartyAccessEntryPageView> {
  const gateway = useGateway();
  return useQuery({
    queryKey: PARTY_ACCESS_QUERY_KEY.entry(voiceAgentId),
    queryFn: () => gateway.listVoiceAgentAccessEntries(voiceAgentId),
    retry: false,
  });
}
